import { FC, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useMixpanel } from 'react-mixpanel-browser';

import { State } from 'redux/initialState';
import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';

import ApiSelector from 'modules/ai/pages/SocietyAiAgent/components/ApiSelector';
import ComposerField from 'modules/ai/components/ComposerField';
import { AI_EVENTS } from 'modules/ai/constants/aiAnalyticsEvents';
import { useSocietyAiAgentPage } from 'modules/ai/pages/SocietyAiAgent/context/SocietyAiAgentPageContext';
import trackAiActivity from 'modules/ai/utils/trackAiActivity';

import isSuperAdmin from 'utils/isSuperAdmin';

type FloatingComposerProps = {
  placeholder?: string;
  autoFocus?: boolean;
};

const FloatingComposer: FC<FloatingComposerProps> = ({
  placeholder = 'CHATBOT_InputPlaceholder',
  autoFocus = false,
}) => {
  const { t } = useTranslate();
  const mixpanel = useMixpanel();
  const hasTrackedFocusRef = useRef(false);
  const { data, actions } = useSocietyAiAgentPage();

  const user = useSelector((state: State) => state.user) || null;

  const leadingSlot = isSuperAdmin(user) && (
    <>
      <ApiSelector />
      <div className="navi-composer__divider" />
    </>
  );

  const handleFocus = () => {
    if (hasTrackedFocusRef.current) return;
    hasTrackedFocusRef.current = true;
    trackAiActivity({
      mixpanel,
      state: store.getState() as State,
      eventName: AI_EVENTS.COMPOSER_FOCUSED,
      extras: {
        surface: 'agent_page',
      },
    });
  };

  return (
    <ComposerField
      variant="agent"
      value={data.inputText}
      onChange={actions.handleChangeInputText}
      onSubmit={actions.handleSendMessage}
      onFocus={handleFocus}
      placeholder={t(placeholder)}
      disabled={data.isThinking}
      autoFocus={autoFocus}
      leadingSlot={leadingSlot}
    />
  );
};

export default FloatingComposer;
